import { Plus } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';

const ColorSwatchPicker = ({
  value,
  disabled,
  onChange,
  idPrefix,
  allTextColors,
  DEFAULT_TEXT_COLORS,
  customColors,
  setCustomColors,
  colorInputRef,
  showCustomInput,
  setShowCustomInput,
}) => {
  const [customText, setCustomText] = useState(value || '#000000');

  useEffect(() => {
    setCustomText(value || '#000000');
  }, [value]);

  useEffect(() => {
    const inputEl = colorInputRef.current;
    if (!inputEl) return;

    const handleCommit = (e) => {
      const color = e.target.value.toUpperCase();
      if (!DEFAULT_TEXT_COLORS.includes(color) && !customColors.includes(color)) {
        setCustomColors((prev) => [...prev, color]);
      }
    };

    inputEl.addEventListener('change', handleCommit);
    return () => inputEl.removeEventListener('change', handleCommit);
  }, [colorInputRef, DEFAULT_TEXT_COLORS, customColors, setCustomColors]);

  const rafRef = useRef(null);
  const pendingColorRef = useRef(null);

  const handleColorDrag = (e) => {
    const color = e.target.value.toUpperCase();
    pendingColorRef.current = color;

    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        onChange(pendingColorRef.current);
        rafRef.current = null;
      });
    }
  };

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const isValidHex = (v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v);

  const commitCustomColor = () => {
    if (!isValidHex(customText)) {
      setCustomText(value || "#000000");
      return;
    }
    const color = customText.toUpperCase();
    if (!DEFAULT_TEXT_COLORS.includes(color) && !customColors.includes(color)) {
      setCustomColors((prev) => [...prev, color]);
    }
    onChange(color);
    setShowCustomInput(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 300 }}>
      <div className="color-swatch-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {allTextColors?.map((c) => (
          <button
            key={`${idPrefix}-${c}`}
            type="button"
            title={c}
            disabled={disabled}
            onClick={() => {
              onChange(c);
              setShowCustomInput(false);
            }}
            style={{
              width: 26, height: 26, borderRadius: '50%', backgroundColor: c,
              border: value === c ? '2px solid #333' : '1px solid #ccc',
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: 0,
            }}
          />
        ))}

        <button
          type="button"
          title="Custom color"
          disabled={disabled}
          onClick={() => colorInputRef.current?.click()}
          style={{
            width: 26, height: 26, borderRadius: '50%',
            border: '1px dashed #999', backgroundColor: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1, padding: 0,
          }}
        >
          <Plus size={12} color="#666" />
        </button>

        <input
          ref={colorInputRef}
          type="color"
          value={value || "#000000"}
          disabled={disabled}
          onChange={handleColorDrag}
          style={{ position: "absolute", opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
        />
      </div>

      {showCustomInput && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 20, height: 20, borderRadius: '50%',
              backgroundColor: isValidHex(customText) ? customText : '#fff',
              border: '1px solid #ccc', flexShrink: 0,
            }}
          />
          <input
            type="text"
            className="form-input"
            placeholder="#RRGGBB"
            value={customText}
            disabled={disabled}
            onChange={(e) => setCustomText(e.target.value)}
            onBlur={commitCustomColor}
            onKeyDown={(e) => { if (e.key === 'Enter') commitCustomColor(); }}
            style={{ width: 100, fontSize: 13 }}
          />
        </div>
      )}
    </div>
  );
};

export default ColorSwatchPicker;
